/**
 * Herói nacional de presidente nas regiões do AppShell (§7.1; FE §5.5, §5.8): o `HeroCard` com as partes das seções
 * donas (§7.5–§7.11, §10.8), dentro da `ErrorBoundary name="hero"` (§7.1, §12.8). Fica fora do App.tsx para que a
 * composição do herói e a do mapa (Fase 5) não disputem as mesmas linhas.
 *
 * Onde aparece (§7.1 bloco 2): `left` no desktop e na TV (sempre, com ou sem foco: o herói nunca muda com a seleção,
 * F43); `card` no tablet, no `land` e no `flow`, só sem lugar em foco e sem perfil (com foco, o cartão é o do §8).
 * Só na aba Presidente. O herói depende só de `snapshot.json` + `candidates.json` (§15.7.4): o 1º turno entra quando
 * o estático chega (etapa 3) e, antes disso, a linha simplesmente não existe (AC-7.8-02).
 */

import { useRef } from 'preact/hooks';
import { t } from '../copy/strings';
import { ErrorBoundary } from '../features/errors/ErrorBoundary';
import { FirstRoundRef, presidentFirstRound } from '../features/hero/FirstRoundRef';
import { GoalBar } from '../features/hero/GoalBar';
import { type HeroBodyContext, HeroCard, heroFlags } from '../features/hero/HeroCard';
import { HeroKpis } from '../features/hero/HeroKpis';
import { HeroLeaders } from '../features/hero/HeroLeaders';
import { MarginRows, marginRowsProps } from '../features/hero/MarginRows';
import { heroModel } from '../features/hero/model';
import { PreResultsGrid } from '../features/hero/PreResultsGrid';
import { StillCountingList } from '../features/hero/StillCountingList';
import { Countdown } from '../features/timeline/Countdown';
import { useHasFocus, useHeadline, useLayout, useRouteState, useStatics, useView } from '../state/hooks';
import { ChanceFocusSlot, useChanceFocusPlace } from './chanceFocusSlot';
import { CHANCE_LAYOUT } from './chanceLayout';

/** Janela `intro` da abertura (§3.6 A02): 1,8 s desde o início da navegação, só no desktop e na TV. */
export const INTRO_WINDOW_MS = 1800;

/** Região do AppShell em que o herói está montado. */
export type HeroRegion = 'left' | 'card';

/** O herói aparece nesta região, nesta rota? (§7.1 bloco 2; o AppShell decide se a região existe no modo.) */
export function heroVisible(region: HeroRegion, office: string, hasFocus: boolean): boolean {
  if (office !== 'president') return false;
  return region === 'left' || !hasFocus;
}

export function PresidentHero({ region }: { region: HeroRegion }) {
  const route = useRouteState();
  const hasFocus = useHasFocus();
  const view = useView();
  const { mode } = useLayout();
  const headline = useHeadline();
  const firstRound = useStatics().firstRound;
  // ORD-F-151 B (dois lados): com a chance no cartão, o apurado ganha o rótulo da sua metade ("Votos contados agora")
  const twin = useChanceFocusPlace() === 'hero' && CHANCE_LAYOUT === 'B';
  // A02 (§3.6.3, §7.2 bloco 5), só na primeira montagem dentro da janela (desktop e TV), cada decisão tomada uma vez:
  // `enter` na montagem do cartão (kf-enter com 450 ms de atraso, só na coluna esquerda); `intro` no primeiro snapshot
  // com números (contagem de 90% a 100%). Herói que some (outra aba) e volta é montagem nova, sem intro.
  const enter = useRef<boolean | null>(null);
  const intro = useRef<boolean | null>(null);
  const desktopLike = mode === 'desktop' || mode === 'tv';
  if (!heroVisible(region, route.office, hasFocus)) {
    if (enter.current !== null) enter.current = false;
    if (intro.current !== null) intro.current = false;
    return null;
  }
  if (enter.current === null) {
    enter.current = region === 'left' && desktopLike && performance.now() < INTRO_WINDOW_MS;
  }
  const numbers = !heroFlags(view, mode, false, headline).pre;
  if (numbers && intro.current === null) {
    intro.current = desktopLike && performance.now() < INTRO_WINDOW_MS;
  }

  const leaders = (c: HeroBodyContext) => (
    <>
      {twin ? (
        <p class="hero-twin-label" data-testid="hero-twin-label">
          {t('prob.twin.counted')}
        </p>
      ) : null}
      <HeroLeaders sides={heroModel(c.view).sides} early={c.early} intro={c.intro} />
    </>
  );
  const goal = (c: HeroBodyContext) => {
    const m = heroModel(c.view);
    return <GoalBar sides={m.sides} decided={m.decided !== null} decimals={m.goalDecimals} />;
  };
  // §7.7: "Vantagem" só em pontos com a manchete EXIBIDA em `leading` (às 17h00:00 o estado acompanha o relógio, G-10)
  const margin = (c: HeroBodyContext) => <MarginRows {...marginRowsProps(c.view)} headline={headline} />;
  // chance no topo: a chance em foco quando ela mora no cartão principal (§7.14 R1 nova, DECISOES §71); senão, e
  // enquanto o chunk não chega, a faixa compacta (C01, K04), só com a chance ativa
  const chance = () => <ChanceFocusSlot region="hero" />;
  const first = (c: HeroBodyContext) => <FirstRoundRef entries={presidentFirstRound(firstRound, c.view.candidates)} />;
  const still = (c: HeroBodyContext) => <StillCountingList decided={c.decided} />;
  const kpis = (c: HeroBodyContext) => (
    <HeroKpis
      validVotes={c.view.national.validVotes}
      turnoutRate={c.view.national.turnoutRate}
      blankNullRate={c.view.national.blankNullRate}
      intro={c.intro}
    />
  );
  // ORD-F-151 A (palco): no celular, no tablet e no computador estreito, o cartão da chance logo acima do herói
  const stage = CHANCE_LAYOUT === 'A' ? <ChanceFocusSlot region="top" /> : null;
  return (
    <>
      {stage}
      <ErrorBoundary name="hero">
        <HeroCard
          pre={<PreResultsGrid />}
          countdown={<Countdown />}
          leaders={leaders}
          goal={goal}
          margin={margin}
          chance={chance}
          firstRound={first}
          stillCounting={still}
          kpis={kpis}
          intro={intro.current === true}
          introEnter={enter.current === true}
        />
      </ErrorBoundary>
    </>
  );
}
